import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpenCheck,
  CalendarCheck2,
  Check,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Flame,
  GraduationCap,
  RefreshCw,
  TrendingUp
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import type { CompoundData } from "../types";

type CalendarCell = {
  date: string;
  count: number;
  checked: boolean;
  future: boolean;
};

function localDateKey(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
}

function shortDate(value: string) {
  const [, month, day] = value.split("-");
  return Number(month) + "月" + Number(day) + "日";
}

function activityLevel(count: number) {
  if (count <= 0) return "l0";
  if (count === 1) return "l1";
  if (count <= 3) return "l2";
  return "l3";
}

function buildCalendarWeeks(calendar: CompoundData["calendar"], todayKey: string) {
  const countByDay = new Map(calendar.map((item) => [item.day, item]));
  const today = new Date(todayKey + "T12:00:00");
  const currentWeekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  currentWeekStart.setDate(currentWeekStart.getDate() - ((currentWeekStart.getDay() + 6) % 7));
  const start = new Date(currentWeekStart);
  start.setDate(start.getDate() - 77);

  const cells: CalendarCell[] = [];
  for (let index = 0; index < 84; index += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = localDateKey(date);
    const entry = countByDay.get(key);
    cells.push({
      date: key,
      count: Number(entry?.count ?? 0),
      checked: Boolean(entry?.checked),
      future: key > todayKey
    });
  }

  return Array.from({ length: 12 }, (_, weekIndex) =>
    cells.slice(weekIndex * 7, weekIndex * 7 + 7)
  );
}

function CalendarGrid({
  calendar,
  todayKey,
  pendingDate,
  onToggle
}: {
  calendar: CompoundData["calendar"];
  todayKey: string;
  pendingDate: string | null;
  onToggle: (day: string, completed: boolean) => void;
}) {
  const [pageIndex, setPageIndex] = useState(2);
  const weeks = useMemo(() => buildCalendarWeeks(calendar, todayKey), [calendar, todayKey]);
  const visibleWeeks = weeks.slice(pageIndex * 4, pageIndex * 4 + 4);
  const firstDate = visibleWeeks[0]?.[0]?.date ?? "";
  const lastDate = visibleWeeks.at(-1)?.at(-1)?.date ?? "";
  const weekdays = ["一", "二", "三", "四", "五", "六", "日"];

  return (
    <div className="action-calendar">
      <div className="action-calendar__toolbar">
        <button
          type="button"
          className="action-calendar__nav"
          onClick={() => setPageIndex((value) => Math.max(0, value - 1))}
          disabled={pageIndex === 0}
          aria-label="查看更早四周"
        >
          <ChevronLeft size={17} />
        </button>
        <span className="action-calendar__range">{shortDate(firstDate)} — {shortDate(lastDate)}</span>
        <button
          type="button"
          className="action-calendar__nav"
          onClick={() => setPageIndex((value) => Math.min(2, value + 1))}
          disabled={pageIndex === 2}
          aria-label="查看更近四周"
        >
          <ChevronRight size={17} />
        </button>
      </div>

      <div className="action-calendar__scroll">
        <table className="action-calendar__table">
          <caption className="sr-only">最近十二周行动记录，可点击日期确认或撤销当日完成</caption>
          <thead>
            <tr>
              {weekdays.map((weekday) => <th key={weekday} scope="col">周{weekday}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleWeeks.map((week) => (
              <tr key={week[0]?.date}>
                {week.map((cell) => {
                  const isToday = cell.date === todayKey;
                  const saving = pendingDate === cell.date;
                  const disabled = cell.future || Boolean(pendingDate);
                  const stateText = cell.checked
                    ? "已确认完成，点击可撤销"
                    : cell.future
                      ? "未来日期，暂不可操作"
                      : "未确认，点击标记完成";
                  return (
                    <td key={cell.date}>
                      <button
                        type="button"
                        className={[
                          "action-calendar__day",
                          activityLevel(cell.count),
                          cell.checked ? "is-checked" : "",
                          isToday ? "is-today" : "",
                          saving ? "is-saving" : ""
                        ].filter(Boolean).join(" ")}
                        data-date={cell.date}
                        aria-pressed={cell.checked}
                        aria-busy={saving || undefined}
                        aria-label={shortDate(cell.date) + "，" + stateText + "，共 " + cell.count + " 条行动事实"}
                        title={shortDate(cell.date) + " · " + stateText}
                        disabled={disabled}
                        onClick={() => onToggle(cell.date, !cell.checked)}
                      >
                        <span className="action-calendar__date">{Number(cell.date.slice(-2))}</span>
                        {cell.checked ? <Check className="action-calendar__check" size={16} aria-hidden="true" /> : null}
                        {cell.count > 0 ? <span className="action-calendar__count">{cell.count}</span> : null}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="action-calendar__hint">点击日期确认完成；带 ✓ 的日期可再次点击撤销。未来日期保持锁定。</p>
    </div>
  );
}

function updateCalendarOptimistically(
  calendar: CompoundData["calendar"],
  day: string,
  completed: boolean
) {
  const next = calendar.map((item) => ({ ...item }));
  const index = next.findIndex((item) => item.day === day);
  if (index >= 0) {
    const current = next[index]!;
    const delta = completed === current.checked ? 0 : completed ? 1 : -1;
    next[index] = {
      ...current,
      checked: completed,
      count: Math.max(0, Number(current.count) + delta)
    };
  } else if (completed) {
    next.push({ day, count: 1, checked: true });
  }
  return next
    .filter((item) => item.count > 0 || item.checked)
    .sort((left, right) => left.day.localeCompare(right.day));
}

export function CompoundPanel() {
  const { dataRevision, refresh } = useApp();
  const requestSequence = useRef(0);
  const [data, setData] = useState<CompoundData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const result = await api.getCompoundData();
      if (!result) throw new Error("复利数据暂不可用");
      if (sequence !== requestSequence.current) return;
      setData(result);
      setLoadError(null);
    } catch (error) {
      if (sequence === requestSequence.current) setLoadError(error instanceof Error ? error.message : "复利数据读取失败");
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
    return () => { ++requestSequence.current; };
  }, [dataRevision, loadData]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    void loadData();
  };

  const toggleDailyAction = async (day: string, completed: boolean) => {
    if (!data || pendingDate) return;
    const previous = data;
    setPendingDate(day);
    setActionError(null);
    setData({ ...data, calendar: updateCalendarOptimistically(data.calendar, day, completed) });

    try {
      await api.setDailyActionCompletion(day, completed);
      const [, nextCompound] = await Promise.all([refresh(), api.getCompoundData()]);
      if (nextCompound) setData(nextCompound);
    } catch (error) {
      setData(previous);
      setActionError(error instanceof Error ? error.message : "没有保存成功，请重试");
    } finally {
      setPendingDate(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="compound-panel compound-panel--loading" aria-busy="true" aria-label="正在读取复利面板">
        <div className="compound-panel__state">
          <RefreshCw size={19} aria-hidden="true" />
          <strong>正在读取行动记录</strong>
          <span>日历、经验和技能证据正在汇总。</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="compound-panel compound-panel--error" role="alert">
        <div className="compound-panel__state">
          <CalendarCheck2 size={19} aria-hidden="true" />
          <strong>复利面板暂时没有读取成功</strong>
          <span>{loadError ?? "请检查网络后重试。"}</span>
          <button type="button" className="compound-panel__retry" onClick={retry}>重新读取</button>
        </div>
      </div>
    );
  }

  const totalXp = data.xpTotals?.total_xp ?? data.skills.reduce((sum, skill) => sum + Number(skill.total_xp ?? 0), 0);
  const weekXp = data.xpTotals?.week_xp ?? data.skills.reduce((sum, skill) => sum + Number(skill.week_xp ?? 0), 0);

  return (
    <div className="compound-panel">
      <div className="compound-hero">
        <div className="compound-hero__icon"><TrendingUp size={22} /></div>
        <div>
          <h3>复利面板</h3>
          <p>微小的行动重复足够久，就会成为人生的分水岭。</p>
        </div>
        <span className="compound-hero__days">
          {data.profile?.daysSinceOrder !== null && data.profile?.daysSinceOrder !== undefined
            ? "低谷第 " + data.profile.daysSinceOrder + " 天 · 已增值"
            : "成长进行中"}
        </span>
      </div>

      <div className="compound-stats">
        <div className="compound-stat"><GraduationCap size={16} /><strong>{data.learning.completed_days}</strong><span>累计打卡</span></div>
        <div className="compound-stat"><Flame size={16} /><strong>{data.streak.week_streak}</strong><span>近7天打卡日</span></div>
        <div className="compound-stat"><TrendingUp size={16} /><strong>+{weekXp}</strong><span>近7天 XP</span></div>
        <div className="compound-stat"><Clapperboard size={16} /><strong>{data.works.total_works}</strong><span>作品</span></div>
      </div>

      <div className="compound-skills">
        <h4><BookOpenCheck size={14} /> 技能成长 <small>总 XP {totalXp}</small></h4>
        {data.skills.length ? data.skills.slice(0, 4).map((skill) => (
          <div key={skill.title} className="compound-skill">
            <div className="compound-skill__head">
              <span>{skill.title}</span>
              <small>{skill.level_title}{Number(skill.week_xp) > 0 ? " · 近7天 +" + skill.week_xp : ""}</small>
            </div>
            <div className="compound-skill__bar" title={`当前等级进度 ${Number(skill.level_progress ?? 0)}%，距下一级 ${Number(skill.xp_to_next_level ?? 0)} XP`}>
              <i style={{ width: Math.min(100, Math.max(0, Number(skill.level_progress ?? 0))) + "%" }} />
            </div>
          </div>
        )) : (
          <p className="compound-skills__empty">完成关联技能的真实任务后，这里会出现第一条成长曲线。</p>
        )}
      </div>

      <div className="compound-calendar">
        <h4><CalendarCheck2 size={14} /> 行动日历 <small>最近 12 周 · 每页 4 周</small></h4>
        <CalendarGrid calendar={data.calendar ?? []} todayKey={data.today} pendingDate={pendingDate} onToggle={toggleDailyAction} />
        <p className="action-calendar__status" aria-live="polite">
          {actionError ?? (pendingDate ? "正在保存这一天的行动状态…" : loadError ?? "")}
        </p>
      </div>
    </div>
  );
}
